import React from 'react';
import './Skeleton.css';

export interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  radius?: string | number;
  variant?: 'text' | 'rectangular' | 'circular';
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  width,
  height,
  radius,
  variant = 'rectangular',
  className = '',
}) => (
  <span
    className={`skeleton skeleton-${variant} ${className}`.trim()}
    aria-hidden="true"
    style={{
      width,
      height,
      borderRadius: radius,
    }}
  />
);

export interface SkeletonTextProps {
  lines?: number;
  widths?: Array<string | number>;
  lineHeight?: string | number;
  gap?: string | number;
  className?: string;
}

export const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 3,
  widths = ['100%', '86%', '64%'],
  lineHeight = 12,
  gap = 8,
  className = '',
}) => (
  <span
    className={`skeleton-text ${className}`.trim()}
    style={{ gap }}
    aria-hidden="true"
  >
    {Array.from({ length: lines }, (_, index) => (
      <Skeleton
        key={index}
        variant="text"
        width={widths[index % widths.length]}
        height={lineHeight}
      />
    ))}
  </span>
);

export const SkeletonButton: React.FC<{ width?: string | number; height?: string | number; className?: string }> = ({
  width = 112,
  height = 36,
  className = '',
}) => <Skeleton width={width} height={height} radius={6} className={className} />;

export const SkeletonCard: React.FC<{ className?: string; dark?: boolean }> = ({ className = '', dark = false }) => (
  <div className={`skeleton-card ${dark ? 'skeleton-surface-dark' : ''} ${className}`.trim()} aria-hidden="true">
    <div className="skeleton-card-body">
      <Skeleton width="68%" height={16} radius={4} />
      <SkeletonText lines={2} widths={['100%', '78%']} lineHeight={10} gap={7} />
      <div className="skeleton-card-actions">
        {Array.from({ length: 3 }, (_, index) => (
          <SkeletonButton key={index} width="31%" height={34} />
        ))}
      </div>
    </div>
  </div>
);

export const SkeletonTable: React.FC<{ columns?: number; rows?: number; className?: string }> = ({
  columns = 5,
  rows = 6,
  className = '',
}) => (
  <div className={`skeleton-table ${className}`.trim()} aria-hidden="true">
    <div className="skeleton-table-row skeleton-table-header" style={{ '--skeleton-columns': columns } as React.CSSProperties}>
      {Array.from({ length: columns }, (_, index) => <Skeleton key={index} width={index === 0 ? '42%' : '68%'} height={12} />)}
    </div>
    {Array.from({ length: rows }, (_, row) => (
      <div className="skeleton-table-row" key={row} style={{ '--skeleton-columns': columns } as React.CSSProperties}>
        {Array.from({ length: columns }, (_, column) => (
          <Skeleton key={column} width={column === 0 ? '74%' : column === columns - 1 ? 28 : '56%'} height={12} />
        ))}
      </div>
    ))}
  </div>
);

export const SkeletonEditor: React.FC<{ label?: string }> = ({ label = 'Loading editor' }) => (
  <section className="skeleton-editor" aria-busy="true" aria-label={label} role="status">
    <span className="sr-only">{label}</span>
    <aside className="skeleton-editor-properties" aria-hidden="true">
      <Skeleton width="58%" height={22} radius={4} />
      <SkeletonText lines={1} widths={['34%']} lineHeight={11} />
      <div className="skeleton-editor-fields">
        {Array.from({ length: 6 }, (_, index) => (
          <div className="skeleton-editor-field" key={index}>
            <Skeleton width={index % 2 === 0 ? '42%' : '58%'} height={11} radius={3} />
            <Skeleton height={38} radius={4} />
          </div>
        ))}
      </div>
    </aside>
    <nav className="skeleton-editor-tabs" aria-hidden="true">
      <Skeleton width="46%" height={14} radius={3} />
      <div className="skeleton-editor-tab-list">
        {Array.from({ length: 8 }, (_, index) => (
          <Skeleton key={index} width={index % 3 === 0 ? '88%' : '74%'} height={44} radius={8} />
        ))}
      </div>
    </nav>
    <div className="skeleton-editor-data" aria-hidden="true">
      <div className="skeleton-editor-toolbar">
        <SkeletonButton width={118} height={34} />
        <SkeletonButton width={104} height={34} />
      </div>
      <Skeleton width="48%" height={13} radius={3} className="skeleton-editor-summary" />
      <SkeletonTable columns={6} rows={9} />
    </div>
  </section>
);

export const SkeletonPage: React.FC<{
  label?: string;
  variant?: 'content' | 'auth' | 'sheet';
  children?: React.ReactNode;
}> = ({ label = 'Loading content', variant = 'content', children }) => (
  <section className={`skeleton-page skeleton-page-${variant}`} aria-busy="true" aria-label={label} role="status">
    <span className="sr-only">{label}</span>
    {children ?? <SkeletonText lines={4} />}
  </section>
);

export const SkeletonGrid: React.FC<{ count?: number; dark?: boolean; className?: string }> = ({ count = 6, dark = false, className = '' }) => (
  <div className={`skeleton-grid ${className}`.trim()} aria-hidden="true">
    {Array.from({ length: count }, (_, index) => <SkeletonCard key={index} dark={dark} />)}
  </div>
);
